import { forwardRef, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Collapse, Stack, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import BuildIcon from '@mui/icons-material/Build';
import FlagIcon from '@mui/icons-material/Flag';
import { useFlags } from 'launchdarkly-react-client-sdk';
import formatDate, { formatUtcDate } from '../../../../utils/formatDate';
import { FOCUSED_CHECKBOX_STYLE, TABLE_CELL_TYPOGRAPHY_STYLES } from '../styles';
import {
    SelectionStatus,
    useInvoicingSelectionContext,
} from '../../../Contexts/InvoicingSelectionContext';
import HierarchyConnectorHorizontal from '../HierarchyConnectorHorizontal';
import AnimatedExpandIcon from '../../../../Shared/Components/AnimatedExpandIcon';
import useHierarchyConnectorVertical from '../hooks/useHierarchyConnectorVertical';
import { currencyFormatter, decimalFormatter } from '../../../../utils/helpers';
import { COLORS, DATE_FORMATS } from '../../../../utils/Application_Constants';
import CollapsibleDataGridWithConnectorLines from '../CollapsibleDataGridWithConnectorLines';
import {
    InvoiceItemsTypeEnum,
    PARTIAL_PTS_VISIBILITY_CONTEXT,
} from '../../../Configurations/invoicingConfig';
import { useShiftSelectionContext } from '../ShiftSelectionContext';
import PartialPtsVisibilityMessages, {
    PartialPtsVisibilityIndicator,
} from '../PartialPtsVisibility';

const timesheetLineItemColumns = [
    {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        valueFormatter: (value) => (value ? formatUtcDate(value, DATE_FORMATS.WEEKDAY_MD) : '-'),
        cellClassName: 'bold-text',
    },
    {
        field: 'rateType',
        headerName: 'Rate Type',
        flex: 1,
        valueFormatter: (value) => value || '—',
    },
    {
        field: 'billTime',
        headerName: 'Bill Time',
        flex: 1,
        sortable: false,
        cellClassName: 'left-border',
        valueFormatter: (value) => (value ? decimalFormatter(value) : '-'),
    },
    {
        field: 'billUnit',
        headerName: 'Bill Unit',
        flex: 1,
        sortable: false,
        valueFormatter: (value) => value || '-',
    },
    {
        field: 'billRate',
        headerName: 'Bill Rate',
        flex: 1,
        sortable: false,
        valueFormatter: (value, row) => {
            if (!value) return '-';
            if (row?.rateType === 'Mileage')
                return `$${Number(value).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4,
                })}`;
            return currencyFormatter(value);
        },
    },
    {
        field: 'billAmount',
        headerName: 'Bill Amount',
        flex: 1,
        sortable: false,
        valueFormatter: (value) => (value ? currencyFormatter(value) : '-'),
    },
];

const getExpenseColumns = (enableExtraInfo) => [
    {
        field: 'isCorrection',
        headerName: '',
        width: 35,
        sortable: false,
        renderCell: (cell) =>
            cell.value === true ? (
                <Tooltip title="This expense has corrections">
                    <BuildIcon
                        sx={{
                            color: COLORS.LT_POMEGRANATE,
                            verticalAlign: 'middle',
                            height: 17,
                            width: 17,
                        }}
                    />
                </Tooltip>
            ) : (
                ''
            ),
    },
    ...(enableExtraInfo
        ? [
              {
                  field: 'id',
                  headerName: 'Exp Delta ID',
                  flex: 1,
                  minWidth: 110,
                  renderCell: (params) => (params.row.isDelta ? params.value : '-'),
              },
          ]
        : []),
    {
        field: 'expenseId',
        headerName: 'Expense ID',
        flex: 1,
        minWidth: 110,
        sortable: true,
        valueFormatter: (value) => value || '—',
    },
    {
        field: 'expenseClass',
        headerName: 'Expense Source',
        flex: 1,
        sortable: false,
        valueFormatter: (value) => value || '—',
    },
    {
        field: 'transactionDate',
        headerName: 'Txn Date',
        flex: 1,
        sortable: true,
        valueFormatter: (value) => (value ? formatUtcDate(value) : '-'),
    },
    {
        field: 'startDate',
        headerName: 'Start Date',
        flex: 1,
        sortable: true,
        valueFormatter: (value) => (value ? formatUtcDate(value) : '-'),
        headerClassName: 'left-border',
        cellClassName: 'left-border',
    },
    {
        field: 'endDate',
        headerName: 'End Date',
        flex: 1,
        sortable: true,
        valueFormatter: (value) => (value ? formatUtcDate(value) : '-'),
    },
    {
        field: 'type',
        headerName: 'Expense Type',
        flex: 1,
        sortable: false,
        valueFormatter: (value) => value || '—',
        headerClassName: 'left-border',
        cellClassName: 'left-border',
    },
    {
        field: 'billAmount',
        headerName: 'Bill Amount',
        flex: 1,
        sortable: false,
        valueFormatter: (value) => (value ? currencyFormatter(value) : '-'),
        headerClassName: 'left-border',
        cellClassName: 'left-border',
    },
];

const InvoicingItemCollapse = (
    {
        hierarchyConnectorVerticalRef: parentHierarchyConnectorVerticalRef,
        bookingId,
        type,
        itemId,
        data,
        disableSelection = false,
        handleSelectItem,
    },
    ref
) => {
    const connectorParentRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpand = () => {
        setIsExpanded((prevValue) => !prevValue);
    };

    const { ltoeWebInvoicingEnableExpensesExtraInfo } = useFlags();
    const expenseItemColumns = useMemo(
        () => getExpenseColumns(ltoeWebInvoicingEnableExpensesExtraInfo),
        [ltoeWebInvoicingEnableExpensesExtraInfo]
    );

    const {
        mainContainerRef,
        expandIconRef,
        lastChildRef,
        hierarchyConnectorVerticalRef,
        renderConnector,
    } = useHierarchyConnectorVertical();

    const { getBookingItemSelectionState } = useInvoicingSelectionContext();
    const selectionStatus = getBookingItemSelectionState(bookingId, type, itemId);
    const { isItemAnchored } = useShiftSelectionContext();

    const hasSubItems =
        (type === InvoiceItemsTypeEnum.Timesheet && data.lineItems?.length > 0) ||
        (type === InvoiceItemsTypeEnum.Expense && data.items?.length > 0);

    // Memoize high priority calculations to prevent re-execution on each render
    const hasHighPriorityExpenseItems = useMemo(
        () => data.items?.some((item) => item.isHighPriority) || false,
        [data.items]
    );

    return (
        <Box ref={mainContainerRef} position="relative">
            <Stack
                ref={ref}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                height={80}
                spacing={1}
                sx={{ cursor: hasSubItems ? 'pointer' : 'default' }}
                onClick={hasSubItems ? toggleExpand : undefined}
                aria-expanded={hasSubItems ? isExpanded : undefined}
                aria-controls={hasSubItems ? `subitem-collapse-${itemId}` : undefined}
            >
                <Box>
                    <Box
                        ref={connectorParentRef}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative',
                            width: hasSubItems ? 74 : 43,
                            marginLeft: !hasSubItems ? '31px' : undefined,
                        }}
                    >
                        <HierarchyConnectorHorizontal
                            anchorRef={connectorParentRef}
                            verticalRef={parentHierarchyConnectorVerticalRef}
                        />
                        {hasSubItems && (
                            <AnimatedExpandIcon ref={expandIconRef} isExpanded={isExpanded} />
                        )}
                        {!disableSelection && (
                            <Checkbox
                                sx={
                                    isItemAnchored(bookingId, itemId)
                                        ? FOCUSED_CHECKBOX_STYLE
                                        : null
                                }
                                checked={selectionStatus === SelectionStatus.SELECTED}
                                indeterminate={selectionStatus === SelectionStatus.INDETERMINATE}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectItem(type, itemId, data?.weekEnding, e);
                                }}
                            />
                        )}
                    </Box>
                </Box>
                {type === InvoiceItemsTypeEnum.Timesheet && (
                    <>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                width: '9.5%',
                                maxWidth: 208,
                            }}
                        >
                            Timesheets&nbsp;({data.lineItems?.length || 0})
                        </Typography>

                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            sx={{
                                width: 68,
                                marginLeft: '5px',
                            }}
                        >
                            {data.isHighPriority && (
                                <Tooltip title="High Priority" placement="top" arrow>
                                    <FlagIcon
                                        sx={{
                                            color: COLORS.LT_PEPPER125,
                                            verticalAlign: 'middle',
                                            height: 17,
                                            width: 17,
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {data.isCorrection && (
                                <Tooltip title="This timesheet has corrections">
                                    <BuildIcon
                                        sx={{
                                            color: COLORS.LT_POMEGRANATE,
                                            verticalAlign: 'middle',
                                            height: 17,
                                            width: 17,
                                        }}
                                    />
                                </Tooltip>
                            )}
                            <PartialPtsVisibilityIndicator
                                context={PARTIAL_PTS_VISIBILITY_CONTEXT.Initiation}
                                timesheet={data}
                            />
                        </Stack>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                fontSize: 12,
                                fontWeight: 400,
                                '& span': { fontSize: 14, fontWeight: 600 },
                                width: '13.6%',
                                maxWidth: 232,
                            }}
                        >
                            Week ending:&nbsp;
                            <span>{formatUtcDate(data.weekEnding)}</span>
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                fontSize: 12,
                                fontWeight: 400,
                                lineHeight: '16px',
                                '& span': { fontSize: 14 },
                                flexGrow: 1,
                                maxWidth: '70%',
                            }}
                        >
                            Totals to Bill:&nbsp;
                            <span>${data.totals?.totalToBill.toFixed(2)}</span>
                            {/* &nbsp;/ Pay:&nbsp;
                            <span>${data.totals?.totalToPay.toFixed(2)}</span> */}
                        </Typography>
                    </>
                )}
                {type === InvoiceItemsTypeEnum.Deposit && (
                    <>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                width: '10.5%',
                                maxWidth: 208,
                            }}
                        >
                            Deposit
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                '& span': { fontWeight: 700 },
                                width: '13.6%',
                                maxWidth: 232,
                            }}
                        >
                            <span>Amount:</span> ${Number(data.amount).toFixed(2)}
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                '& span': { fontWeight: 700 },
                                flexGrow: 1,
                            }}
                        >
                            <span>Posted date:</span> {formatDate(data.postedDate)}
                        </Typography>
                    </>
                )}
                {type === InvoiceItemsTypeEnum.Expense && (
                    <>
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{
                                width: '9.5%',
                                maxWidth: 208,
                            }}
                        >
                            <Typography sx={{ ...TABLE_CELL_TYPOGRAPHY_STYLES }}>
                                Expenses
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 1 }}>
                                {hasHighPriorityExpenseItems && (
                                    <Tooltip title="High Priority" placement="top" arrow>
                                        <FlagIcon
                                            sx={{
                                                color: COLORS.LT_PEPPER125,
                                                verticalAlign: 'middle',
                                                height: 17,
                                                width: 17,
                                            }}
                                        />
                                    </Tooltip>
                                )}
                                {data.items?.some((item) => item.isCorrection) && (
                                    <Tooltip title="This expense has corrections">
                                        <BuildIcon
                                            sx={{
                                                color: COLORS.LT_POMEGRANATE,
                                                verticalAlign: 'middle',
                                                height: 17,
                                                width: 17,
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </Stack>
                        </Stack>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                fontSize: 12,
                                fontWeight: 400,
                                '& span': { fontSize: 14, fontWeight: 600 },
                                width: '13.6%',
                                maxWidth: 232,
                            }}
                        >
                            <span>BKG-{bookingId}</span>
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                fontSize: 12,
                                fontWeight: 400,
                                lineHeight: '16px',
                                '& span': { fontSize: 14 },
                                flexGrow: 1,
                                maxWidth: '70%',
                            }}
                        >
                            Total to bill:&nbsp;
                            {currencyFormatter(data?.totals?.totalToBill)}
                        </Typography>
                    </>
                )}
            </Stack>

            {type === InvoiceItemsTypeEnum.Timesheet && (
                <PartialPtsVisibilityMessages
                    context={PARTIAL_PTS_VISIBILITY_CONTEXT.Initiation}
                    timesheet={data}
                    sx={{ ml: '74px', mr: 2, mb: 1 }}
                />
            )}

            {hasSubItems && (
                <Collapse in={isExpanded} id={`subitem-collapse-${itemId}`}>
                    {renderConnector(isExpanded)}
                    <Box sx={{ ml: 0, px: '26px' }}>
                        {type === InvoiceItemsTypeEnum.Timesheet && (
                            <CollapsibleDataGridWithConnectorLines
                                bookingId={bookingId}
                                title={data.splitByWorkLocation ? data.locationName : ''}
                                itemId={data.id}
                                itemType={InvoiceItemsTypeEnum.Timesheet}
                                columns={timesheetLineItemColumns}
                                rows={data.lineItems}
                                hierarchyConnectorVerticalRef={hierarchyConnectorVerticalRef}
                                lastChildRef={lastChildRef}
                                disableSelection={disableSelection}
                                isExpanded={isExpanded}
                                isSelectable={false}
                            />
                        )}
                        {type === InvoiceItemsTypeEnum.Expense && (
                            <CollapsibleDataGridWithConnectorLines
                                bookingId={bookingId}
                                itemId={data.id}
                                itemType={InvoiceItemsTypeEnum.Expense}
                                columns={expenseItemColumns}
                                rows={data.items}
                                hierarchyConnectorVerticalRef={hierarchyConnectorVerticalRef}
                                lastChildRef={lastChildRef}
                                disableSelection={disableSelection}
                                isExpanded={isExpanded}
                                sx={{ maxWidth: '900px' }}
                            />
                        )}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};

export default forwardRef(InvoicingItemCollapse);
